import { Suspense, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useLazyLoadQuery } from "react-relay"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { LoadingSpinner } from "@/components/loading-spinner"
import type { Country } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import type { chPatientByPatientIdQuery as ChPatientByPatientIdQueryType } from "@/modules/redirect/services/ch/__generated__/chPatientByPatientIdQuery.graphql"
import { chPatientByPatientIdQuery } from "@/modules/redirect/services/ch/chPatientByPatientIdQuery"
import type { dePatientByPatientIdQuery as DePatientByPatientIdQueryType } from "@/modules/redirect/services/de/__generated__/dePatientByPatientIdQuery.graphql"
import { dePatientByPatientIdQuery } from "@/modules/redirect/services/de/dePatientByPatientIdQuery"
import { CountryValues } from "@/types/__generated__/union-types"

const VALID_TARGETS = ["agenda"] as const

const PatientRedirectContent = ({
  country,
  patientId,
  target,
}: {
  country: Country
  patientId: string
  target: string
}) => {
  const navigate = useNavigate()
  const query = country === "CH" ? chPatientByPatientIdQuery : dePatientByPatientIdQuery
  const data = useLazyLoadQuery<ChPatientByPatientIdQueryType | DePatientByPatientIdQueryType>(query, { patientId })

  const relayId = data.patientByPatientId?.id

  useEffect(() => {
    if (relayId) {
      const redirectPath = `/patients/${country}/${relayId}/${target}`
      void navigate(redirectPath, { replace: true })
    }
  }, [navigate, country, relayId, target])

  if (!relayId) {
    return <PatientNotFound />
  }

  return <RedirectingSpinner />
}

const PatientNotFound = () => {
  const { t } = useTranslation()
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4">
      <p className="text-muted-foreground text-lg">{t("redirect.patientNotFound")}</p>
    </div>
  )
}

const RedirectingSpinner = () => {
  const { t } = useTranslation()
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4">
      <LoadingSpinner size={24} />
      <p className="text-muted-foreground text-sm">{t("redirect.redirecting")}</p>
    </div>
  )
}

export const PatientRedirect = () => {
  const { country, patientId } = useParams()
  const [searchParams] = useSearchParams()
  const target = searchParams.get("target") ?? "agenda"
  const { t } = useTranslation()

  const upperCountry = country?.toUpperCase() as Country

  if (!country || !patientId || !CountryValues.includes(upperCountry)) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground text-lg">{t("redirect.invalidParameters")}</p>
      </div>
    )
  }

  if (!VALID_TARGETS.includes(target as (typeof VALID_TARGETS)[number])) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground text-lg">{t("redirect.invalidTarget")}</p>
      </div>
    )
  }

  return (
    <Suspense fallback={<RedirectingSpinner />}>
      <PatientRedirectContent country={upperCountry} patientId={patientId} target={target} />
    </Suspense>
  )
}
